import React, { createContext, useState, useContext, useCallback, useRef } from 'react';

// Create the context
const GlassyNotificationsContext = createContext();

// Create a provider component
export const GlassyNotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const notificationCounter = useRef(0);

  const generateUniqueId = useCallback(() => {
    notificationCounter.current += 1;
    return `glassy-${Date.now()}-${notificationCounter.current}`;
  }, []);

  const notify = useCallback((options) => {
    const {
      title,
      message,
      type = 'info',
      position = 'top-center',
      duration = 4000,
      id,
      icon,
      animation = 'spring'
    } = options;

    const notificationId = id || generateUniqueId();
    
    const notification = {
      id: notificationId,
      title,
      message,
      type,
      position,
      icon,
      animation,
      duration,
      timestamp: Date.now()
    };

    setNotifications(prev => {
      // Remove existing notification with same id if it exists
      const filtered = prev.filter(notif => notif.id !== notificationId);
      // Add new notification
      return [...filtered, notification];
    });

    return notificationId;
  }, [generateUniqueId]);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Promise-based notification for async operations
  const notifyPromise = useCallback(async (promise, options) => {
    const {
      loading = 'Loading...',
      success = 'Operation completed successfully',
      error = 'Operation failed',
      position = 'top-center'
    } = options;

    const loadingId = notify({
      message: loading,
      type: 'info',
      position,
      duration: 0 // Don't auto-dismiss loading
    });

    try {
      const result = await promise;
      removeNotification(loadingId);
      notify({
        message: success,
        type: 'success',
        position
      });
      return result;
    } catch (err) {
      removeNotification(loadingId);
      notify({
        message: error,
        type: 'error',
        position
      });
      throw err;
    }
  }, [notify, removeNotification]);

  return (
    <GlassyNotificationsContext.Provider 
      value={{ 
        notify,
        removeNotification,
        clearAllNotifications,
        notifyPromise,
        notifications
      }}
    >
      {children}
      <GlassyNotificationsContainer notifications={notifications} onRemove={removeNotification} />
    </GlassyNotificationsContext.Provider>
  );
};

// Custom hook for using the context
export const useGlassyNotifications = () => {
  const context = useContext(GlassyNotificationsContext);
  if (!context) {
    throw new Error('useGlassyNotifications must be used within a GlassyNotificationsProvider');
  }
  return context;
};

// Glassy Notifications Container Component
const GlassyNotificationsContainer = ({ notifications, onRemove }) => {
  const getPositionStyles = (position) => {
    const baseStyles = {
      position: 'fixed',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      pointerEvents: 'none'
    };

    switch (position) {
      case 'top-left':
        return { ...baseStyles, top: '20px', left: '20px' };
      case 'top-right':
        return { ...baseStyles, top: '20px', right: '20px' };
      case 'bottom-left':
        return { ...baseStyles, bottom: '20px', left: '20px' };
      case 'bottom-right':
        return { ...baseStyles, bottom: '20px', right: '20px' };
      case 'top-center':
        return { ...baseStyles, top: '20px', left: '50%', transform: 'translateX(-50%)' };
      case 'bottom-center':
        return { ...baseStyles, bottom: '20px', left: '50%', transform: 'translateX(-50%)' };
      default:
        return { ...baseStyles, top: '20px', right: '20px' };
    }
  };

  const getTypeStyles = (type) => {
    const baseStyles = {
      border: '1px solid rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      padding: '16px 20px',
      minWidth: '320px',
      maxWidth: '400px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      pointerEvents: 'auto',
      position: 'relative',
      overflow: 'hidden'
    };

    switch (type) {
      case 'success':
        return {
          ...baseStyles,
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.05))',
          borderColor: 'rgba(34, 197, 94, 0.3)'
        };
      case 'error':
        return {
          ...baseStyles,
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05))',
          borderColor: 'rgba(239, 68, 68, 0.3)'
        };
      case 'warning':
        return {
          ...baseStyles,
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.05))',
          borderColor: 'rgba(245, 158, 11, 0.3)'
        };
      case 'info':
      default:
        return {
          ...baseStyles,
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05))',
          borderColor: 'rgba(59, 130, 246, 0.3)'
        };
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success':
        return '#22c55e';
      case 'error':
        return '#ef4444';
      case 'warning':
        return '#f59e0b';
      case 'info':
      default:
        return '#3b82f6';
    }
  };

  // Group notifications by position
  const notificationsByPosition = notifications.reduce((acc, notification) => {
    if (!acc[notification.position]) {
      acc[notification.position] = [];
    }
    acc[notification.position].push(notification);
    return acc;
  }, {});

  return (
    <>
      {Object.entries(notificationsByPosition).map(([position, positionNotifications]) => (
        <div key={position} style={getPositionStyles(position)}>
          {positionNotifications.map((notification) => (
            <GlassyNotificationItem
              key={notification.id}
              notification={notification}
              onRemove={onRemove}
              typeStyles={getTypeStyles(notification.type)}
              typeIcon={getTypeIcon(notification.type)}
              typeColor={getTypeColor(notification.type)}
            />
          ))}
        </div>
      ))}
      
      <style jsx>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideInLeft {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideInTop {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideInBottom {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
            transform: scale(1);
          }
          to {
            opacity: 0;
            transform: scale(0.95);
          }
        }
      `}</style>
    </>
  );
};

// Individual Glassy Notification Item
const GlassyNotificationItem = ({ notification, onRemove, typeStyles, typeIcon, typeColor }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isExiting, setIsExiting] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const timeoutRef = React.useRef(null);
  const remainingRef = React.useRef(notification.duration || 0);
  const startTimeRef = React.useRef(null);

  React.useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleRemove = () => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(notification.id);
    }, 300); // Match animation duration
  };

  // Auto-dismiss handling with hover pause and accurate remaining time
  React.useEffect(() => {
    if (!notification.duration || notification.duration <= 0) return;

    const clearTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const startTimer = (delay) => {
      clearTimer();
      startTimeRef.current = Date.now();
      timeoutRef.current = setTimeout(() => {
        handleRemove();
      }, delay);
    };

    // Initialize timer on mount
    if (startTimeRef.current === null) {
      remainingRef.current = notification.duration;
      startTimer(remainingRef.current);
    }

    if (isHovered) {
      // Pause: update remaining time and clear timer
      const elapsed = Date.now() - (startTimeRef.current || Date.now());
      remainingRef.current = Math.max(0, remainingRef.current - elapsed);
      clearTimer();
    } else if (!isExiting) {
      // Resume with remaining time
      if (remainingRef.current > 0) {
        startTimer(remainingRef.current);
      }
    }

    return () => clearTimer();
  }, [notification.duration, isHovered, isExiting]);

  const getAnimationStyle = () => {
    if (isExiting) {
      return {
        animation: 'slideOut 0.3s ease-in-out forwards'
      };
    }

    if (isVisible) {
      const position = notification.position;
      if (position.includes('right')) {
        return { animation: 'slideInRight 0.4s ease-out forwards' };
      } else if (position.includes('left')) {
        return { animation: 'slideInLeft 0.4s ease-out forwards' };
      } else if (position.includes('top')) {
        return { animation: 'slideInTop 0.4s ease-out forwards' };
      } else {
        return { animation: 'slideInBottom 0.4s ease-out forwards' };
      }
    }

    return { opacity: 0 };
  };

  return (
    <div 
      style={{
        ...typeStyles,
        ...getAnimationStyle(),
        cursor: 'pointer'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleRemove}
    >
      {/* Progress bar for auto-dismiss */}
      <div 
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: '2px',
          background: typeColor,
          width: '100%',
          transformOrigin: 'left',
          animationName: notification.duration > 0 ? 'progress' : 'none',
          animationDuration: `${notification.duration}ms`,
          animationTimingFunction: 'linear',
          animationFillMode: 'forwards',
          animationPlayState: isHovered ? 'paused' : 'running'
        }}
      />

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Icon */}
        <div 
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: typeColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold',
            flexShrink: 0
          }}
        >
          {notification.icon || typeIcon}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {notification.title && (
            <h4 style={{ 
              margin: 0, 
              fontSize: '14px', 
              fontWeight: '600', 
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '4px'
            }}>
              {notification.title}
            </h4>
          )}
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: '1.4'
          }}>
            {notification.message}
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRemove();
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '16px',
            lineHeight: 1,
            flexShrink: 0,
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = 'rgba(255, 255, 255, 0.8)';
          }}
          onMouseLeave={(e) => {
            e.target.style.color = 'rgba(255, 255, 255, 0.5)';
          }}
        >
          ×
        </button>
      </div>

      <style jsx>{`
        @keyframes progress {
          from {
            transform: scaleX(1);
          }
          to {
            transform: scaleX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default GlassyNotificationsContext;
